import { reactive } from 'vue'
import findIndex from 'lodash/findIndex'

import layerService from '@/services/layer-service'
import { getItem, saveItem } from '@/plugins/storage'
import {
  lessThanOrEquals,
  greaterThanOrEquals,
  equalsExpression,
  all,
  has,
  not,
} from '@/services/viewer-filter'
import { FILTER_DEFAULTS } from '@/services/filter-layer-service/constants'

import { getMode } from '@/lib/modes'

import { Vertical } from '@/types/verticals'
import { FishMode, type Mode } from '@/types/modes'
import type {
  Checkbox,
  CheckboxFilter,
  MinMaxValue,
  FilterState,
  FilterableVerticals,
} from '@/services/filter-layer-service/types.ts'

const ACTIVITY_KEY = 'activity'

/**
 * Filters in use per app mode
 */
const activeFilters = reactive<FilterState>({
  [Vertical.Fish]: {
    [FishMode.Freshwater]: [],
  },
})

/**
 * Current Activity Types selected (ski, xc ski, etc)
 */
const activityTypeFilters = reactive<string[]>([])

/**
 * Get list of activity filters
 * @returns The reactive list of active activity type filter values
 */
export const getActivityTypeFilters = () => activityTypeFilters

/**
 * Get count of currently active activity filters
 * @returns The number of active activity type filters
 */
export const getActivityTypeFiltersLength = () =>
  getActivityTypeFilters().length

/**
 * Get current filters in use for app and mode
 * @param app
 * @param mode
 * @returns List of all active filters
 */
export const getActiveFilters = (app: FilterableVerticals, mode: Mode) => [
  ...new Set(activeFilters[app][mode]),
]

/**
 * Get filters in use for current app and mode
 * @param app
 * @param mode
 * @returns List of all active filters
 */
export const getCurrentActiveFilters = () => {
  const { appName, appMode } = getAppNameAndMode()
  return getActiveFilters(appName, appMode)
}

/**
 * Get length of active filters list
 * @returns length of list
 */
export const getActiveFiltersLength = () => {
  const { appName, appMode } = getAppNameAndMode()
  return getActiveFilters(appName, appMode).length
}

/**
 * Get current app and mode
 */
export const getAppNameAndMode = () => {
  return {
    appName: 'fish' as FilterableVerticals,
    appMode: getMode() as Mode,
  }
}

/**
 * Service for modifying, storing, and sharing the client filter values
 */

/**
 * Utility to set the active filter map based on defaults when the page loads, before mode card has been opened
 */
export const checkAndSetFilterPreferences = () => {
  getSliderFilterIds().forEach((filter) => {
    if (hasSliderFilter(filter)) {
      addTotalFilter(filter)
    }
  })

  getCheckboxFilterIds().forEach((filter) => {
    if (hasCheckboxFilter(filter)) {
      addTotalFilter(filter)
    }
  })
}

/**
 * Return default filter values for given app and mode
 * @returns - Filter default obejct
 */
export const getFilterDefaults = () => {
  const { appName, appMode } = getAppNameAndMode()
  return FILTER_DEFAULTS[appName][appMode]
}

/**
 * Get all checkbox filter objects and active statuses, either from preferences or defaults
 * @param includeAll - Include the "All" button value or not
 * @param filterKey - Key corresponding to filter being selected
 * @returns - Object of all Checkbox filters
 */
export const getCheckboxValues = (includeAll: boolean, filterKey: string) => {
  const { appName, appMode } = getAppNameAndMode()
  const storageItems = getItem<CheckboxFilter>(
    `${appName}_${appMode}_${filterKey}`
  )
  // If there are preferences in storage
  if (storageItems) {
    if (includeAll) {
      return storageItems
    }
    // Don't include the `all` object if requested
    // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
    delete storageItems[`${filterKey}_all`]
    return storageItems
  }
  // Else, no preferences, use defaults
  const defaults = getFilterDefaults()
  const items = defaults[filterKey] as CheckboxFilter

  if (includeAll) {
    return items
  }
  // Don't include the `all` object if requested
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete items[`${filterKey}_all`]
  return items
}

/**
 * Get all values of a checkbox filter, save them to localStorage
 * @param filterKey - Key corresponding to filter being selected
 */
export const saveAllCheckboxValues = (filterKey: string) => {
  const { appName, appMode } = getAppNameAndMode()
  const items = getCheckboxValues(true, filterKey)
  saveItem(`${appName}_${appMode}_${filterKey}`, items)
}

/**
 * Save a specific checkbox value on or off
 * @param id - ID to set
 * @param value - Object with id, label, active, displayActive properties
 * @param filterKey - Key corresponding to filter being selected
 */
export const saveCheckbox = (
  id: string,
  value: Checkbox,
  filterKey: string
) => {
  const { appName, appMode } = getAppNameAndMode()
  // Note deep clone of default object, not accessing default value itself
  const defaults = getFilterDefaults()
  const currItem =
    getItem<CheckboxFilter>(`${appName}_${appMode}_${filterKey}`) ??
    structuredClone(defaults[filterKey])
  currItem[id] = value
  saveItem(`${appName}_${appMode}_${filterKey}`, currItem)
}

/**
 * Get a specific checkbox object
 * @param id - ID of the checkbox key object to get (see keys in filter defaults)
 * @param filterKey - Key corresponding to filter being selected
 * @return  - Single object from requested ID
 */
export const getCheckboxValue = (id: string, filterKey: string) => {
  const { appName, appMode } = getAppNameAndMode()
  const defaults = getFilterDefaults()
  // Note deep clone of default object, not accessing default value itself
  const storageItem = getItem<CheckboxFilter>(
    `${appName}_${appMode}_${filterKey}`
  )
  const defaultItem = structuredClone(defaults[filterKey]) as CheckboxFilter
  return storageItem ? storageItem[id] : defaultItem[id]
}

/**
 * Toggle state of checkbox filters
 * @param selected - State of currently selected filter item
 * @param filterKey - Key corresponding to filter being selected
 */
export const toggleCheckbox = (selected: Checkbox, filterKey: string) => {
  const { id } = selected
  const item = getCheckboxValue(id, filterKey)
  item.active = !item.active
  item.displayActive = !item.displayActive
  saveCheckbox(id, item, filterKey)
}

/**
 * Discretely set the state of a given checkbox
 * @param selected - State of currently selected filter item
 * @param bool - Discrete boolean value to use
 * @param filterKey - Key corresponding to filter being selected
 */
export const setCheckbox = (
  selected: Checkbox,
  bool: boolean,
  filterKey: string
) => {
  const { id } = selected
  const item = getCheckboxValue(id, filterKey)
  item.active = bool
  item.displayActive = bool
  saveCheckbox(id, item, filterKey)
}

/**
 * Set the "Display Active" state of a checkbox - when it can be active for filtering purposes, but displayed as unchecked
 * @param selected - State of currently selected filter item
 * @param bool - Discrete boolean value to use
 * @param filterKey - Key corresponding to filter being selected
 */
export const setCheckboxDisplayActive = (
  selected: Checkbox,
  bool: boolean,
  filterKey: string
) => {
  const { id } = selected
  const item = getCheckboxValue(id, filterKey)
  item.active = bool
  item.displayActive = !bool
  saveCheckbox(id, item, filterKey)
}

/**
 * Get the value of a given checkbox filter from localstorage
 * @param app - The vertical app context
 * @param mode - Current app mode
 * @param filterKey - Key corresponding to filter being selected
 * @returns The stored checkbox filter data, or undefined if not set
 */
export const getCheckboxFilterData = (
  app: FilterableVerticals,
  mode: Mode,
  filterKey: string
) => {
  const currFilter = getItem<CheckboxFilter>(`${app}_${mode}_${filterKey}`)
  return Object.values(currFilter)
    ?.filter((i) => i.id !== `${filterKey}_all` && i.active)
    .map((i) => i.label)
}

/**
 * Get the min, max of a given checkbox filter from localstorage
 * @param mode - Current app mode
 * @param filterKey - Key corresponding to filter being selected
 * @returns An array of {min, max}
 */
export const getCheckboxFilterDataMinMax = (
  app: FilterableVerticals,
  mode: Mode,
  filterKey: string
) => {
  const currFilter = getItem<CheckboxFilter>(`${app}_${mode}_${filterKey}`)
  return Object.values(currFilter)
    ?.filter((i) => i.id !== `${filterKey}_all` && i.active)
    .map(({ min, max }) => ({
      min,
      max,
    }))
}

/**
 * Get the value of a given checkbox filter from localstorage
 * @param mode - Current app mode
 * @param filterKey - Key corresponding to filter being selected
 * @returns Array of data ** slugs **
 */
export const getCheckboxFilterDataSlug = (
  app: Vertical,
  mode: Mode,
  filterKey: string
) => {
  const currFilter = getItem(`${app}_${mode}_${filterKey}`)
  if (currFilter) {
    return Object.values(currFilter)
      ?.filter((i) => i.id !== `${filterKey}_all` && i.active)
      .map((i) => i.slug)
  }
  return []
}

/* ACTIVITY FILTERS */
/**
 * Add or remove a filter from the active filters array
 * @param id - ID of filter clicked
 */
export const toggleActivityFilter = (id: string) => {
  if (activityTypeFilters.includes(id)) {
    const index = activityTypeFilters.indexOf(id)
    activityTypeFilters.splice(index, 1)
  } else {
    activityTypeFilters.push(id)
  }
  activityTypeFilters.length === layerService.getActivitiesLengthByMode()
    ? removeTotalFilter(ACTIVITY_KEY)
    : addTotalFilter(ACTIVITY_KEY)
}

/**
 * Add an activity filter intentionally, don't toggle it. Used with "All" button
 * @param id - ID of filter clicked
 */
export const addActivityFilter = (id: string) => {
  if (!activityTypeFilters.includes(id)) {
    activityTypeFilters.push(id)
  }
  if (activityTypeFilters.length !== layerService.getActivitiesLengthByMode()) {
    addTotalFilter(ACTIVITY_KEY)
  } else {
    removeTotalFilter(ACTIVITY_KEY)
  }
}

/**
 * Reset active filter list
 */
export const resetActivityTypeFilters = () => {
  activityTypeFilters.splice(0)
}

/* TOTAL FILTERS */

/**
 * Add item to total filter list
 * @param id - ID of filter type
 */
export const addTotalFilter = (id: string) => {
  const { appName, appMode } = getAppNameAndMode()
  if (!activeFilters[appName][appMode].includes(id)) {
    activeFilters[appName][appMode].push(id)
  }
}

/**
 * Remove item from total filter list
 * @param id - ID of filter type
 */
export const removeTotalFilter = (id: string) => {
  const { appName, appMode } = getAppNameAndMode()
  if (activeFilters[appName][appMode].includes(id)) {
    activeFilters[appName][appMode] = activeFilters[appName][appMode].filter(
      (i: string) => i !== id
    )
  }
}

/**
 * Reset total filter map
 */
export const resetTotalFilters = () => {
  const { appName, appMode } = getAppNameAndMode()
  activeFilters[appName][appMode].splice(0)
}

/**
 * Get count of filters currently applied
 * @returns - Number of filters currently applied
 */
export const getTotalFilterLength = () => {
  const { appName, appMode } = getAppNameAndMode()
  return activeFilters[appName][appMode].length
}

/**
 * Check whether a given filter (using range-slider) is currently being set
 * @param key - Key of filter object to get from storage
 * @returns - If neither min or max filter applied return false. Else return object saying which of min and/or max has filter
 */
export const hasSliderFilter = (key: string) => {
  try {
    const defaults = getFilterDefaults()
    const defaultSlider = defaults[key]
    const { appName, appMode } = getAppNameAndMode()
    if (defaultSlider) {
      const userFilters = getItem(`${appName}_${appMode}_${key}`) as MinMaxValue
      const hasMinFilter = userFilters?.min !== defaultSlider?.min
      const hasMaxFilter = userFilters?.max !== defaultSlider?.max
      if (userFilters && (hasMinFilter || hasMaxFilter)) {
        addTotalFilter(key)
        return {
          hasMinFilter,
          hasMaxFilter,
        }
      }
    } else {
      return false
    }
  } catch {
    /* pass */
  }
  return false
}

/**
 * Get current data from a given slider
 * @param filterKey - filter key to return data on
 * @returns - current min and max values of a slider
 */
export const getSliderFilterData = (filterKey: string) => {
  const { appName, appMode } = getAppNameAndMode()
  const currFilter = getItem(
    `${appName}_${appMode}_${filterKey}`
  ) as MinMaxValue
  const currMin = currFilter?.min
  const currMax = currFilter?.max
  return { min: currMin, max: currMax }
}

/**
 * Get list of default filters per app mode that are checkboxes
 * @returns - list of filter keys
 */
export const getCheckboxFilterIds = (): string[] => {
  const defaults = getFilterDefaults()
  const keys = []
  for (const [key, value] of Object.entries(defaults)) {
    if (value.type !== 'range') {
      keys.push(key)
    }
  }
  return keys
}

/**
 * Get list of default filters per app mode that are sliders
 * @returns - list of filter keys
 */
export const getSliderFilterIds = (): string[] => {
  const defaults = getFilterDefaults()
  const keys = []
  for (const [key, value] of Object.entries(defaults)) {
    if (value.type === 'range') {
      keys.push(key)
    }
  }
  return keys
}

/**
 * Get the keys of filters that need a "+" displayed with them
 * @returns - Array of filter IDs
 */
export const getSliderFilterIdsUsingPlus = () => {
  const defaults = getFilterDefaults()
  const keys = []
  for (const [key, val] of Object.entries(defaults)) {
    val.plus && keys.push(key)
  }
  return keys
}

/**
 * Check if given filter (using checkbox list) is being set
 * @param filterKey - Key of filter object to get from storage
 * @returns - False if no filter, array of filters set if has filter
 */
export const hasCheckboxFilter = (filterKey: string) => {
  const { appName, appMode } = getAppNameAndMode()
  const defaults = getFilterDefaults()
  if (defaults[filterKey]) {
    const userFilters = getItem<CheckboxFilter>(
      `${appName}_${appMode}_${filterKey}`
    )
    let anyInactive = false
    if (userFilters) {
      const includesAll = userFilters[`${filterKey}_all`]
      const userFilterKeys = Object.values(userFilters)
      if (!includesAll) {
        anyInactive = findIndex(userFilterKeys, 'active') !== -1
      } else {
        anyInactive = userFilterKeys.some(
          (item) => item.id !== `${filterKey}_all` && !item.active
        )
      }
    }
    anyInactive && addTotalFilter(filterKey)
    return anyInactive ? userFilters : false
  }
  return false
}

/**
 * *(AI-generated)*
 * Returns Mapbox filter expressions for a checkbox filter group.
 */
export const getCheckboxFilterExpressions = (
  filterKey: string,
  tileKey?: string
) => {
  const filter = hasCheckboxFilter(filterKey)

  if (!filter) {
    return
  }

  if (!tileKey) {
    return
  }

  const filterExpressions: unknown[] = []

  Object.values(filter).forEach((checkbox) => {
    // Ignore any inactive checkboxes, and the all checkbox
    if (!checkbox.active || checkbox.id === `${filterKey}_all`) {
      return
    }
    // For unknown, include where the tilekey isn't defined
    if (checkbox.id === `${filterKey}_unknown`) {
      filterExpressions.push(not(has(tileKey)))
      return
    }

    // If the checkbox has a min/max (difficulty) use them. Min and max can be 0.
    if (!isNaN(Number(checkbox.min)) && !isNaN(Number(checkbox.max))) {
      filterExpressions.push(
        all(
          greaterThanOrEquals(tileKey, checkbox.min),
          lessThanOrEquals(tileKey, checkbox.max)
        )
      )
      return
    }

    // If the checkbox has individual tile keys for each value, check them
    if (checkbox.tileKey) {
      filterExpressions.push(greaterThanOrEquals(checkbox.tileKey, 1))
      return
    }

    // For everything else, filter for the tilekey to match the checkbox label
    filterExpressions.push(equalsExpression(tileKey, checkbox.label))
  })

  return filterExpressions
}
